import { ToastQueue } from '@adobe/react-spectrum';

/**
 * Centralized authentication error handler
 * Handles 401 and 403 errors consistently across the application
 */

// Track if we're already handling a session expiration to prevent duplicate toasts
let isHandlingSessionExpiration = false;
let sessionExpirationToastShown = false;

/**
 * Clear authentication token from cookies
 */
const clearAuthToken = () => {
  document.cookie =
    'app_access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  console.log('🔐 Auth token cleared');
};

/**
 * Store current URL for redirect after login
 */
const storeRedirectUrl = () => {
  const currentPath = window.location.pathname + window.location.search;
  // Only store if we're not already on the login/checkLogin page
  if (!currentPath.includes('/checkLogin') && !currentPath.includes('/auth/')) {
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    console.log('🔐 Stored redirect URL for after login:', currentPath);
    return currentPath;
  }
  return null;
};

/**
 * Redirect to Okta authentication
 */
const redirectToAuth = (redirectUrl?: string) => {
  const authUrl =
    process.env.REACT_APP_OKTA_AUTH_URL || 'http://localhost:3000/auth/okta';
  const fullUrl = redirectUrl
    ? `${authUrl}?redirectUrl=${encodeURIComponent(redirectUrl)}`
    : authUrl;

  console.log('🔐 Redirecting to authentication:', fullUrl);
  window.location.replace(fullUrl);
};

/**
 * Clear all toast messages
 */
const clearAllToasts = () => {
  try {
    // Clear toast queue by closing all active toasts
    // Note: ToastQueue doesn't have a direct clear method, but we can prevent new toasts
    // The redirect will clear the UI anyway
  } catch (error) {
    // Silently fail if toast clearing fails
  }
};

/**
 * Reset session expiration tracking (call this after successful login)
 */
export const resetSessionExpirationTracking = () => {
  isHandlingSessionExpiration = false;
  sessionExpirationToastShown = false;
};

/**
 * Handle 401 Unauthorized errors
 * Shows user-friendly message and redirects to login
 * Prevents duplicate toasts and handles multiple 401 errors gracefully
 */
export const handle401Error = (context?: string) => {
  // Prevent multiple simultaneous 401 handlers
  if (isHandlingSessionExpiration) {
    console.log(
      '🔐 Session expiration already being handled, skipping duplicate handler'
    );
    return;
  }

  // Check if we're already on the login/checkLogin page
  const currentPath = window.location.pathname;
  if (currentPath.includes('/checkLogin') || currentPath.includes('/auth/')) {
    console.log(
      '🔐 Already on login page, skipping session expiration handler'
    );
    return;
  }

  isHandlingSessionExpiration = true;
  console.error('❌ 401 Unauthorized Error:', context || 'API call failed');

  // Only show toast message once, even if multiple 401 errors occur
  if (!sessionExpirationToastShown) {
    // Show user-friendly toast message with shorter timeout since we're redirecting
    ToastQueue.negative('Your session has expired. Please log in again.', {
      timeout: 3000 // Reduced from 5000 since we redirect quickly
    });
    sessionExpirationToastShown = true;
  }

  // Clear the invalid token
  clearAuthToken();

  // Store redirect URL (only if not already on login page)
  const redirectUrl = storeRedirectUrl();

  // Reduced delay since toast timeout is shorter
  // Redirect quickly to avoid user confusion
  setTimeout(() => {
    redirectToAuth(redirectUrl || undefined);
  }, 500); // Reduced from 1000ms to 500ms
};

/**
 * Handle 403 Forbidden errors
 * Shows user-friendly message about permissions
 */
export const handle403Error = (context?: string) => {
  console.error('❌ 403 Forbidden Error:', context || 'Access denied');

  // Show user-friendly toast message
  ToastQueue.negative(
    "You don't have permission to access this resource. Please contact your administrator.",
    {
      timeout: 5000
    }
  );

  // Don't redirect for 403 - user is authenticated but doesn't have permission
  // They may have permission to access other parts of the app
};

/**
 * Generic authentication error handler
 * Routes to specific handler based on status code
 */
export const handleAuthError = (status: number, context?: string) => {
  switch (status) {
    case 401:
      handle401Error(context);
      break;
    case 403:
      handle403Error(context);
      break;
    default:
      console.warn('⚠️ Unhandled auth error status:', status);
  }
};

/**
 * Check if error is an authentication error (401 or 403)
 */
export const isAuthError = (status: number): boolean => {
  return status === 401 || status === 403;
};

/**
 * Enhanced fetch wrapper that handles authentication errors automatically
 * Use this instead of raw fetch for all API calls
 */
export const fetchWithAuthHandling = async (
  url: string,
  options: RequestInit = {},
  context?: string
): Promise<Response> => {
  try {
    const response = await fetch(url, options);

    // Check for authentication errors
    if (isAuthError(response.status)) {
      handleAuthError(response.status, context || url);
      throw new Error(`Authentication error: ${response.status}`);
    }

    return response;
  } catch (error) {
    // Re-throw the error after handling
    throw error;
  }
};
